import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { getActiveClanAchievements, getClanProgressByClanId } from '../../services/achievementService';
import { Achievement, ClanAchievementProgress } from '../../types/achievement';
import AchievementModal from '../achievements/AchievementModal';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';

const cardBorder = (theme: any) => theme.profile?.cards?.border || theme.colors.borderLight;

interface ClanAchievementsProps {
  clanId: string;
  gameType?: 'airsoft' | 'paintball' | 'all';
  refreshTrigger?: number;
}

// Styled Components
const Container = styled.div`
  width: 100%;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  font-size: 0.9rem;
  padding: 8px 16px;
  background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.surface, 0.8)};
  color: ${props => props.$active ? (isShadowTheme(props.theme) ? '#000000' : '#00121f') : props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.1)};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 14px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  text-align: center;
  backdrop-filter: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 8px;
  text-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.5)};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const AchievementCard = styled.div<{ $completed: boolean; $rarity: string }>`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  border: 1px solid ${props => {
    if (props.$completed) {
      switch (props.$rarity.toLowerCase()) {
        case 'legendary': return '#ff6b35';
        case 'epic': return '#9b59b6';
        case 'rare': return '#3498db';
        case 'uncommon': return '#27ae60';
        default: return '#95a5a6';
      }
    }
    return props.theme.name === 'modernWarfare' ? 'rgba(255, 255, 255, 0.1)' : cardBorder(props.theme);
  }};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  position: relative;
  transition: all 0.3s ease;
  opacity: ${props => props.$completed ? 1 : 0.8};
  cursor: pointer;
  backdrop-filter: none;

  &:hover {
    background: ${({ theme }) => theme.name === 'modernWarfare'
      ? 'rgba(5, 7, 10, 0.8)'
      : 'var(--legacy-card-bg)'};
    border-color: ${props => {
      if (props.$completed) {
        switch (props.$rarity.toLowerCase()) {
          case 'legendary': return '#ff6b35';
          case 'epic': return '#9b59b6';
          case 'rare': return '#3498db';
          case 'uncommon': return '#27ae60';
          default: return '#95a5a6';
        }
      }
      return props.theme.colors.primary;
    }};
    transform: translateX(5px);
    box-shadow: 0 10px 30px ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  }
`;

const AchievementIcon = styled.div<{ $completed: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$completed ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.2)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 24px;
`;

const AchievementName = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 10px 0;
  text-align: center;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const AchievementDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 15px 0;
  text-align: center;
  line-height: 1.6;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AchievementRarity = styled.div<{ $rarity: string }>`
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 0.7rem;
  padding: 6px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  font-weight: bold;
  background: ${props => {
    switch (props.$rarity.toLowerCase()) {
      case 'legendary': return '#ff6b35';
      case 'epic': return '#9b59b6';
      case 'rare': return '#3498db';
      case 'uncommon': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 8px 14px;
  }
`;

const AchievementProgressContainer = styled.div`
  margin-top: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: ${({ theme }) => theme.gradients.primary};
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const CompletedBadge = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 30px;
  height: 30px;
  background: ${({ theme }) => theme.colors.success};
  border: 2px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin: 40px 0;
  font-size: 1.2rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const NoAchievementsMessage = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 60px 20px;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 40px 15px;
  }
`;

const ClanAchievements: React.FC<ClanAchievementsProps> = ({ clanId, gameType = 'all', refreshTrigger }) => {
  const theme = useTheme();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [progressData, setProgressData] = useState<ClanAchievementProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'completed' | 'in-progress'>('all');
  const [rarityFilter, setRarityFilter] = useState<'all' | 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'>('all');
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadAchievements();
  }, [clanId, gameType, refreshTrigger]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setError(null);

      const [achievementsData, progressData] = await Promise.all([
        getActiveClanAchievements(),
        getClanProgressByClanId(clanId)
      ]);

      setAchievements(achievementsData);
      setProgressData(progressData);
    } catch (err) {
      console.error('Error loading clan achievements:', err);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  const getAchievementProgress = (achievementId: string): ClanAchievementProgress | null => {
    return progressData.find(p => p.achievementId === achievementId) || null;
  };

  const calculateProgress = (achievement: Achievement, progress: ClanAchievementProgress | null): number => {
    if (!progress || progress.isCompleted) return progress?.isCompleted ? 100 : 0;

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'clan_win_games':
      case 'clan_play_games':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'clan_win_games_streak':
      case 'clan_play_games_streak':
        return Math.min(100, (progressInfo.currentStreak / conditions.count) * 100);
      case 'clan_members_count':
        return Math.min(100, (progressInfo.memberCount / conditions.count) * 100);
      case 'clan_complete_all_missions':
        return Math.min(100, (progressInfo.completedMissions.length / conditions.count) * 100);
      default:
        return 0;
    }
  };

  const getProgressText = (achievement: Achievement, progress: ClanAchievementProgress | null): string => {
    if (!progress) return `0 / ${achievement.conditions.count}`;
    if (progress.isCompleted) return 'Completed!';

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'clan_win_games':
        return `${progressInfo.dailyCount} / ${conditions.count} wins`;
      case 'clan_play_games':
        return `${progressInfo.dailyCount} / ${conditions.count} games`;
      case 'clan_win_games_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} win streak`;
      case 'clan_play_games_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} game streak`;
      case 'clan_members_count':
        return `${progressInfo.memberCount} / ${conditions.count} members`;
      case 'clan_complete_all_missions':
        return `${progressInfo.completedMissions.length} / ${conditions.count} missions`;
      default:
        return `0 / ${conditions.count}`;
    }
  };

  const filteredAchievements = achievements.filter(achievement => {
    // Game type filter
    if (gameType !== 'all' && achievement.gameType !== 'any' && achievement.gameType !== gameType) {
      return false;
    }

    // Completion filter
    const progress = getAchievementProgress(achievement.id);
    const isCompleted = progress?.isCompleted || false;
    
    if (filter === 'completed' && !isCompleted) return false;
    if (filter === 'in-progress' && isCompleted) return false;

    // Rarity filter
    if (rarityFilter !== 'all' && achievement.rarity.toLowerCase() !== rarityFilter) {
      return false;
    }

    return true;
  });

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setShowModal(true);
  };

  const stats = {
    total: achievements.filter(a => gameType === 'all' || a.gameType === 'any' || a.gameType === gameType).length,
    completed: achievements.filter(a => {
      const gameMatches = gameType === 'all' || a.gameType === 'any' || a.gameType === gameType;
      const progress = getAchievementProgress(a.id);
      return gameMatches && progress?.isCompleted;
    }).length,
    inProgress: achievements.filter(a => {
      const gameMatches = gameType === 'all' || a.gameType === 'any' || a.gameType === gameType;
      const progress = getAchievementProgress(a.id);
      return gameMatches && progress && !progress.isCompleted;
    }).length
  };

  if (loading) {
    return <LoadingMessage>Loading clan achievements...</LoadingMessage>;
  }

  if (error) {
    return <NoAchievementsMessage>{error}</NoAchievementsMessage>;
  }

  return (
    <Container>
      {/* Stats */}
      <StatsContainer>
        <StatCard>
          <StatValue>{stats.completed}</StatValue>
          <StatLabel>Earned</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.inProgress}</StatValue>
          <StatLabel>In Progress</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total Available</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</StatValue>
          <StatLabel>Completion Rate</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Filters */}
      <FilterContainer>
        <FilterGroup>
          <FilterButton 
            $active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            All
          </FilterButton>
          <FilterButton 
            $active={filter === 'completed'} 
            onClick={() => setFilter('completed')}
          >
            Earned
          </FilterButton>
          <FilterButton 
            $active={filter === 'in-progress'} 
            onClick={() => setFilter('in-progress')}
          >
            In Progress
          </FilterButton>
        </FilterGroup>

        <FilterGroup>
          <FilterButton 
            $active={rarityFilter === 'all'} 
            onClick={() => setRarityFilter('all')}
          >
            All Rarity
          </FilterButton>
          <FilterButton 
            $active={rarityFilter === 'common'} 
            onClick={() => setRarityFilter('common')}
          >
            Common
          </FilterButton>
          <FilterButton 
            $active={rarityFilter === 'uncommon'} 
            onClick={() => setRarityFilter('uncommon')}
          >
            Uncommon
          </FilterButton>
          <FilterButton 
            $active={rarityFilter === 'rare'} 
            onClick={() => setRarityFilter('rare')}
          >
            Rare
          </FilterButton>
          <FilterButton 
            $active={rarityFilter === 'epic'} 
            onClick={() => setRarityFilter('epic')}
          >
            Epic
          </FilterButton>
          <FilterButton 
            $active={rarityFilter === 'legendary'} 
            onClick={() => setRarityFilter('legendary')}
          >
            Legendary
          </FilterButton>
        </FilterGroup>
      </FilterContainer>

      {/* Achievements Grid */}
      {filteredAchievements.length === 0 ? (
        <NoAchievementsMessage>
          No achievements found for the selected filters.
        </NoAchievementsMessage>
      ) : (
        <AchievementsGrid>
          {filteredAchievements.map(achievement => {
            const progress = getAchievementProgress(achievement.id);
            const isCompleted = progress?.isCompleted || false;
            const progressPercent = calculateProgress(achievement, progress);
            const progressText = getProgressText(achievement, progress);

            return (
              <AchievementCard
                key={achievement.id}
                $completed={isCompleted}
                $rarity={achievement.rarity}
                onClick={() => handleAchievementClick(achievement)}
              >
                {isCompleted && (
                  <CompletedBadge>✓</CompletedBadge>
                )}
                
                <AchievementRarity $rarity={achievement.rarity}>
                  {achievement.rarity}
                </AchievementRarity>

                <AchievementIcon $completed={isCompleted}>
                  {achievement.iconUrl ? (
                    <img src={achievement.iconUrl} alt={achievement.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  ) : (
                    isCompleted ? '🏆' : '⚔️'
                  )}
                </AchievementIcon>

                <AchievementName>{achievement.name}</AchievementName>
                <AchievementDescription>{achievement.description}</AchievementDescription>

                {!isCompleted && (
                  <AchievementProgressContainer>
                    <ProgressBar>
                      <ProgressFill $progress={progressPercent} />
                    </ProgressBar>
                    <ProgressText>{progressText}</ProgressText>
                  </AchievementProgressContainer>
                )}

                {isCompleted && progress?.completedAt && (
                  <ProgressText>
                    Earned: {new Date(progress.completedAt).toLocaleDateString()}
                  </ProgressText>
                )}
              </AchievementCard>
            );
          })}
        </AchievementsGrid>
      )}

      {/* Achievement Modal */}
      {selectedAchievement && (
        <AchievementModal
          achievement={selectedAchievement}
          progress={getAchievementProgress(selectedAchievement.id)}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedAchievement(null);
          }}
        />
      )}
    </Container>
  );
};

export default ClanAchievements;